import { motion } from "framer-motion";
import { ArrowRight, FileText, RefreshCw, BarChart3, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO, PAGE_SEO } from "@/components/SEO";

const JOIN_URL = "https://app.lifely.guide/join/pricing";

const useCases = [
  {
    number: "01",
    title: "Use Lifely to Prepare for a Will",
    subtitle: "Before you meet a lawyer, you need clarity.",
    description: "Lifely helps you gather everything important in one structured place — so you're not trying to remember it all in a single appointment.",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&q=80&auto=format&fit=crop",
    imageAlt: "Documents and planning",
    icon: FileText,
    documentList: [
      "Arrangements for children and guardians",
      "Care plans for pets",
      "Bank accounts and policies",
      "Trust structures",
      "Property and vehicles",
      "Valuables and collectibles",
      "Share portfolios",
      "Crypto assets and wallets",
    ],
    highlight: "For crypto holders especially, this matters. If a seed phrase disappears, so does the asset.",
    conclusion: "Lifely doesn't store sensitive credentials — but it helps you clearly signal where those responsibilities exist. It turns a vague \"I think I've got everything covered\" into structured confidence.",
  },
  {
    number: "02",
    title: "A Living Companion to Your Will",
    subtitle: "A will is legally binding. Life isn't static.",
    description: "Careers change. Investments evolve. Crypto positions move. Agreements happen informally. Value appears in unexpected places. Lifely is designed to evolve with you.",
    image: "https://images.unsplash.com/photo-1614179787386-b1d557c715f2?w=800&h=600&q=80&auto=format&fit=crop",
    imageAlt: "Road leading forward through mountains",
    icon: RefreshCw,
    documentList: [
      "Self-custody crypto positions",
      "Staking arrangements on decentralised exchanges",
      "Experimental investments",
      "Handshake agreements",
      "Syndicate ownership interests",
      "Art collections",
      "Livestock or agricultural interests",
      "Anything with value that others may not realise exists",
    ],
    highlight: "Some of these details may never make it into a formal will. But they matter.",
    conclusion: "Lifely gives you a place to articulate context — not just ownership, but intent. Because complexity grows faster than most people update legal documents.",
  },
  {
    number: "03",
    title: "Use Lifely as a Portfolio Clarity Tool",
    subtitle: "Modern portfolios aren't simple.",
    description: "You might have shares across multiple brokers, crypto across several exchanges, ETH staked in one place, SOL staked on a Ledger, a small position in Bittensor, tokens sitting in a DeFi protocol, a fractional aircraft syndicate share.",
    image: "https://images.pexels.com/photos/669613/pexels-photo-669613.jpeg?w=800&h=600&q=80&auto=format&fit=crop",
    imageAlt: "Laptop with charts and data analysis",
    icon: BarChart3,
    documentList: null,
    highlight: "These \"small experiments\" add up. Over time, it becomes easy to forget where everything lives — especially when assets are spread across custodial and self-custody environments.",
    conclusion: "Lifely gives you one structured view across all of it. Not for trading. Not for custody. But for awareness.",
    benefits: [
      "Clarity reduces risk",
      "Visibility reduces loss",
      "Structure reduces stress",
    ],
  },
];

const whySubscribe = [
  "They want control without complexity",
  "They hold digital assets that require clear signalling",
  "Their financial life is evolving faster than their legal documents",
  "They don't want loved ones guessing",
  "They value privacy and minimal data storage",
  "They want something lightweight but intentional",
];

export default function HowItWorks() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.howItWorks} />
      {/* Hero Section */}
      <section className="py-14 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
          >
            How It Works
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight"
          >
            Three Ways to Use Lifely
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-10 space-y-2 text-lg text-muted-foreground"
          >
            <p>Lifely isn't a will.</p>
            <p>It isn't a vault.</p>
            <p>It isn't a legal service.</p>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-8 text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl mx-auto"
          >
            It's the clarity layer that sits around your life — helping you organise what matters, 
            keep it updated, and reduce uncertainty for the people who may one day need to step in.
          </motion.p>
        </div>
      </section>

      {/* Use Cases */}
      {useCases.map((useCase, index) => (
        <section 
          key={index} 
          className={`py-20 ${index % 2 === 1 ? 'bg-secondary/30' : ''}`}
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:col-start-2' : ''}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-medium text-primary/60">{useCase.number}</span>
                  <useCase.icon className="w-5 h-5 text-primary" />
                </div>
                
                <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
                  {useCase.title}
                </h2>
                
                <p className="text-xl text-primary font-medium mb-4">
                  {useCase.subtitle}
                </p>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {useCase.description}
                </p>

                {useCase.documentList && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-foreground mb-3">Document:</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {useCase.documentList.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 mt-0.5 text-primary/60 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {useCase.benefits && (
                  <div className="mb-6 space-y-2">
                    {useCase.benefits.map((benefit, i) => (
                      <p key={i} className="text-foreground font-medium">{benefit}</p>
                    ))}
                  </div>
                )}

                <div className="bg-primary/5 border border-primary/10 rounded-xl p-5 mb-4">
                  <p className="text-foreground/90 italic">
                    {useCase.highlight}
                  </p>
                </div>

                <p className="text-muted-foreground leading-relaxed">
                  {useCase.conclusion}
                </p>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={useCase.image}
                    alt={useCase.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Why People Subscribe */}
      <section className="py-24 bg-[#2D2A26] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-6">
              Why People Subscribe
            </h2>
            <p className="text-white/70 text-lg">
              People subscribe to Lifely because:
            </p>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-4 max-w-2xl mx-auto"
          >
            {whySubscribe.map((reason, i) => (
              <li key={i} className="flex items-start gap-4">
                <Check className="w-5 h-5 mt-0.5 text-[#E8A882] flex-shrink-0" />
                <span className="text-white/90 text-lg">{reason}</span>
              </li>
            ))}
          </motion.ul>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center text-white/80 text-lg mt-10 max-w-2xl mx-auto"
          >
            Lifely is built for people who understand that knowledge matters — 
            and that clarity is a responsibility.
          </motion.p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl md:text-4xl text-foreground mb-6"
          >
            Start Mapping What Matters
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-muted-foreground mb-4 leading-relaxed"
          >
            Whether you're preparing for a will, maintaining one, or simply trying 
            to keep track of a growing portfolio, Lifely helps you create structure around your life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-2 text-muted-foreground mb-10"
          >
            <p>Start free.</p>
            <p>Upgrade when you're ready.</p>
            <p>Keep clarity on your terms.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <a href={JOIN_URL}>
              <Button className="h-12 px-8 text-sm bg-foreground text-background hover:bg-foreground/90 rounded-full font-medium">
                View Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
